import { DataTestId, TextKey } from '@mews-commander/enums';
import { LocalizedMinorHighlight, LocalizedSmallParagraph } from '@mews-commander/ui';
import {
    atomizeCampaignDataDrivenDecisions as dataDrivenDecisionsImagePath,
    atomizeCampaignMaximizeProfitability as maximizeProfitabilityImagePath,
    atomizeCampaignStreamlineOperations as streamlineOperationsImagePath,
} from '@mews-commander/assets';
import { MewsImage } from '@mews/business-components';
import { Button, Stack } from '@optimus-web/core';
import { LocalizedText, useLanguageCode } from '@mews/framework';
import { Widget } from '@mews-commander/dashboard/widgets';
import { openInBrowserTab } from '@mews-commander/utils';

const AtomizeCampaignUrlMap: Record<string, string> = {
    en: 'https://www.mews.com/en/campaigns/atomize',
    nl: 'https://www.mews.com/nl/campaigns/revenue-management-system',
    fr: 'https://www.mews.com/fr/campaigns/revenue-management-system',
    ge: 'https://www.mews.com/de/campaigns/revenue-management-system',
    es: 'https://www.mews.com/es/campaigns/revenue-management-system',
};
const IMAGE_HEIGHT = '196px';

interface BaseProps {
    imagePath: string;
    headerTextKey: Mews.TextKey;
    bodyTextKey: Mews.TextKey;
    buttonTextKey: Mews.TextKey;
}

const AtomizeCampaignWidgetBase = ({ imagePath, headerTextKey, bodyTextKey, buttonTextKey }: BaseProps) => {
    const languageCode = useLanguageCode();
    const languageCodeWithoutCountry = languageCode?.split('-')[0];
    const url =
        typeof languageCodeWithoutCountry === 'string' && languageCodeWithoutCountry in AtomizeCampaignUrlMap
            ? AtomizeCampaignUrlMap[languageCodeWithoutCountry]
            : AtomizeCampaignUrlMap.en;

    const handleClick = () => openInBrowserTab(url);

    return (
        <Widget titleTextKey={TextKey.DashboardWidgetAtomizeCampaignTitle}>
            <MewsImage
                src={imagePath}
                height={IMAGE_HEIGHT}
                objectFit="contain"
                objectPosition="left top"
                shouldShowLoader
            />
            <LocalizedMinorHighlight textKey={headerTextKey} />
            <LocalizedSmallParagraph textKey={bodyTextKey} />
            <Stack>
                <Button
                    onClick={handleClick}
                    data-test-id={DataTestId.DashboardWidgetAtomizeCampaignButton}
                    type="submit"
                >
                    <LocalizedText textKey={buttonTextKey} />
                </Button>
            </Stack>
        </Widget>
    );
};

export const MaximizeProfitabilityWidget = () => (
    <AtomizeCampaignWidgetBase
        imagePath={maximizeProfitabilityImagePath}
        bodyTextKey={TextKey.DashboardWidgetAtomizeCampaignBodyMaximizeProfitabilityWithoutRMS}
        headerTextKey={TextKey.DashboardWidgetAtomizeCampaignHeaderMaximizeProfitability}
        buttonTextKey={TextKey.DashboardWidgetAtomizeCampaignCTAReadMore}
    />
);

export const StreamlineOperationsWidget = () => (
    <AtomizeCampaignWidgetBase
        imagePath={streamlineOperationsImagePath}
        bodyTextKey={TextKey.DashboardWidgetAtomizeCampaignBodyStreamlineOperationsWithoutRMS}
        headerTextKey={TextKey.DashboardWidgetAtomizeCampaignHeaderStreamlineOperations}
        buttonTextKey={TextKey.DashboardWidgetAtomizeCampaignCTALearnMore}
    />
);

export const DataDrivenDecisionsWidget = () => (
    <AtomizeCampaignWidgetBase
        imagePath={dataDrivenDecisionsImagePath}
        bodyTextKey={TextKey.DashboardWidgetAtomizeCampaignBodyDataDrivenDecisionsWithoutRMS}
        headerTextKey={TextKey.DashboardWidgetAtomizeCampaignHeaderDataDrivenDecisions}
        buttonTextKey={TextKey.DashboardWidgetAtomizeCampaignCTAReadMore}
    />
);
